import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, Gift } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/20 px-3 py-2 text-sm";
const EMPTY = { title: "", first_name: "", last_name: "", house: "", postcode: "" };

export const GiftAidFields = ({ value, onChange, prefix = "gift-aid" }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-2" data-testid={`${prefix}-fields`}>
      <div className="grid grid-cols-[4.5rem_1fr_1fr] gap-2">
        <input data-testid={`${prefix}-title`} placeholder="Title" maxLength={4} value={value.title} onChange={set("title")} className={inputCls} />
        <input data-testid={`${prefix}-first-name`} placeholder="First name" maxLength={35} value={value.first_name} onChange={set("first_name")} className={inputCls} />
        <input data-testid={`${prefix}-last-name`} placeholder="Last name" maxLength={35} value={value.last_name} onChange={set("last_name")} className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid={`${prefix}-house`} placeholder="House name or number" maxLength={40} value={value.house} onChange={set("house")} className={inputCls} />
        <input data-testid={`${prefix}-postcode`} placeholder="Postcode" maxLength={8} value={value.postcode} onChange={set("postcode")} className={`${inputCls} uppercase`} />
      </div>
    </div>
  );
};

export const giftAidComplete = (v) => !!(v.first_name.trim() && v.last_name.trim() && v.house.trim() && v.postcode.trim());

// Online giving: declaration saved once per giver and pre-ticked next time.
export const useGiftAid = () => {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(null);
  const [on, setOn] = useState(false);
  const [details, setDetails] = useState(EMPTY);
  useEffect(() => {
    api.get("/gift-aid/me").then(({ data }) => {
      setText(data.declaration_text);
      if (data.declaration) { setSaved(data.declaration); setOn(true); setDetails({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data.declaration[k] || ""])) }); }
    }).catch(() => {});
  }, []);
  const persist = async () => {
    if (!on) return true;
    if (!giftAidComplete(details)) { toast.error("Please add your name, house and postcode for Gift Aid"); return false; }
    try { const { data } = await api.put("/gift-aid/me", details); setSaved(data.declaration); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const cancel = async () => {
    await api.delete("/gift-aid/me"); setSaved(null); setOn(false); setDetails(EMPTY); toast.success("Gift Aid declaration cancelled");
  };
  return { text, saved, on, setOn, details, setDetails, persist, cancel };
};

export const GiftAidBox = ({ ga, amount }) => (
  <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: ga.on ? "#E0B100" : "var(--sj-border)", background: ga.on ? "#FEF6D8" : "#fff" }} data-testid="gift-aid-box">
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" data-testid="gift-aid-checkbox" checked={ga.on} onChange={(e) => ga.setOn(e.target.checked)} className="h-4 w-4 mt-0.5 accent-[#0B5E4E]" />
      <span className="text-sm">
        <span className="font-semibold flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" style={{ color: "#8a6a00" }} /> Boost my gift with Gift Aid</span>
        <span className="block text-xs" style={{ color: "var(--sj-text-2)" }} data-testid="gift-aid-boost">UK taxpayers: the church can claim an extra 25p for every £1 — £{(Number(amount || 0) * 0.25).toFixed(2)} on this gift, at no cost to you.</span>
      </span>
    </label>
    {ga.on && (<>
      <p className="text-[11px] leading-relaxed" style={{ color: "#5c4a00" }} data-testid="gift-aid-declaration">{ga.text}</p>
      <GiftAidFields value={ga.details} onChange={ga.setDetails} />
      {ga.saved && <button type="button" data-testid="gift-aid-cancel" onClick={ga.cancel} className="text-xs font-semibold underline" style={{ color: "#C0392B" }}>Cancel my Gift Aid declaration</button>}
    </>)}
  </div>
);

export const GiftAidClaim = ({ data }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(`${today.slice(0, 4)}-04-06` > today ? `${Number(today.slice(0, 4)) - 1}-04-06` : `${today.slice(0, 4)}-04-06`);
  const [end, setEnd] = useState(today);
  const download = async () => {
    try {
      const res = await api.get("/donations/gift-aid-claim", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `gift_aid_claim_${start}_to_${end}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Gift Aid claim downloaded");
    } catch (e) { toast.error("Couldn't download the claim"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#EBE9E4] p-6" data-testid="gift-aid-claim">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[14rem]">
          <h3 className="font-head text-xl font-semibold flex items-center gap-2"><Gift className="h-5 w-5" style={{ color: "#8a6a00" }} /> Gift Aid</h3>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>
            <span data-testid="gift-aid-total">£{(data.gift_aid_total || 0).toLocaleString("en-GB")}</span> given with Gift Aid ·
            claimable about <strong data-testid="gift-aid-claimable">£{(data.gift_aid_claimable || 0).toLocaleString("en-GB")}</strong>
          </p>
        </div>
        <label className="text-xs font-semibold" style={{ color: "var(--sj-text-3)" }}>From<input type="date" data-testid="gift-aid-start" value={start} onChange={(e) => setStart(e.target.value)} className={`${inputCls} mt-1`} /></label>
        <label className="text-xs font-semibold" style={{ color: "var(--sj-text-3)" }}>To<input type="date" data-testid="gift-aid-end" value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputCls} mt-1`} /></label>
        <button data-testid="gift-aid-claim-download" onClick={download} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--sj-primary)" }}><Download className="h-4 w-4" /> HMRC claim CSV</button>
      </div>
    </div>
  );
};
